import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { MagnifyingGlass, DownloadSimple, Plus, ImageSquare, Trash, PencilSimple, Tag } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Table, THead, TBody, TR, TH, TD,
  Skeleton, EmptyState, Pagination, Tooltip,
} from "../../components/ui";
import BrandModal from "./BrandModal";
import ImportModal from "./ImportModal";

function Brands() {
  const { checkAccess } = useAccessChecker();

  const [brandModal, setBrandModal] = useState(false);
  const [BrandId, setBrandId] = useState(null);
  const [brands, setBrands] = useState([]);
  const [brandToUpdate, setBrandToUpdate] = useState(null);
  const [ImportBrandModal, setImportBrandModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoding] = useState(false);
  const [done, setDone] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    getBrand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BrandId, done, searchValue]);

  const getBrand = async (page = 1) => {
    try {
      setLoding(true);
      const response = await axiosInstance.post(`/brands/search?page=${page}&per_page=10`, { searchValue });
      const responseData = response.data;
      if (responseData) {
        setBrands(responseData.data || []);
        setCurrentPage(responseData.current_page);
        setTotalPages(responseData.last_page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch brands");
    } finally {
      setLoding(false);
    }
  };

  const handelEditModal = (item) => {
    setBrandToUpdate(item);
    setBrandModal(true);
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Brands"
        subtitle="Manage product brands and manufacturers."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search brands…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              containerClassName="w-full sm:w-64"
            />
            <Button variant="outline" icon={DownloadSimple} onClick={() => setImportBrandModal(true)}>
              Import
            </Button>
            {checkAccess("brands", "create") && (
              <Button icon={Plus} onClick={() => setBrandModal(true)}>
                New brand
              </Button>
            )}
          </>
        }
      />

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Brand</TH>
            <TH>Slug</TH>
            <TH>Created</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                <TD>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <Skeleton className="h-3.5 w-28" />
                  </div>
                </TD>
                <TD><Skeleton className="h-3.5 w-32" /></TD>
                <TD><Skeleton className="h-3.5 w-24" /></TD>
                <TD><Skeleton className="ml-auto h-7 w-16" /></TD>
              </TR>
            ))}

          {!loading &&
            brands.map((brand) => (
              <TR key={brand.id}>
                <TD>
                  <div className="flex items-center gap-3">
                    {brand.image ? (
                      <img src={brand.image} alt="" className="h-10 w-10 rounded-lg border border-line object-cover" />
                    ) : (
                      <div className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-surface-2 text-faint">
                        <ImageSquare size={18} />
                      </div>
                    )}
                    <span className="font-medium text-ink">{brand.name}</span>
                  </div>
                </TD>
                <TD className="text-muted">{brand.slug}</TD>
                <TD className="text-muted">{formatDate(brand.created_at)}</TD>
                <TD>
                  <div className="flex items-center justify-end gap-0.5">
                    {checkAccess("brands", "update") && (
                      <Tooltip label="Edit">
                        <button onClick={() => handelEditModal(brand)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                          <PencilSimple size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {checkAccess("brands", "delete") && (
                      <Tooltip label="Delete">
                        <button
                          onClick={() => {
                            setBrandId(brand.id);
                            setDeleteModal(true);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"
                        >
                          <Trash size={16} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </TD>
              </TR>
            ))}

          {!loading && brands.length === 0 && (
            <tr>
              <td colSpan={4}>
                <EmptyState
                  icon={Tag}
                  title="No brands yet"
                  description="Create your first brand to organize products."
                  action={
                    checkAccess("brands", "create") ? (
                      <Button icon={Plus} onClick={() => setBrandModal(true)}>
                        New brand
                      </Button>
                    ) : null
                  }
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => getBrand(p)}
        summary={`Page ${currentPage} of ${totalPages}`}
      />

      <BrandModal
        isOpen={brandModal}
        onClose={() => setBrandModal(false)}
        BrandToUpdate={brandToUpdate}
        setDone={setDone}
        done={done}
        setBrandToUpdate={setBrandToUpdate}
      />
      <ImportModal
        isOpen={ImportBrandModal}
        onClose={() => setImportBrandModal(false)}
        setDone={setDone}
        done={done}
      />
      <DeleteModal
        setDeleteId={setBrandId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={BrandId}
        Model="brands"
        setDone={setDone}
        setLoding={setLoding}
        done={done}
      />
    </div>
  );
}

export default Brands;
